import { deferAnimationsToFirstPaint } from '@theme/card-enter';
import { prefetchSwapImages } from '@theme/media-swap';

(function () {
  'use strict';

  if (customElements.get('product-list')) return;

  class ProductList extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.transitionGeneration = (this.transitionGeneration || 0) + 1;
      this.transitionPromise = null;
      const { signal } = this.abortController;
      this.reducedMotion = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
      this.responsiveMode = this.getResponsiveMode();
      this.activeAnimations = new Set();
      this.setTransitioning(false);

      this.addEventListener('dieux:tabs:change', this.handleTabChange, { signal });
      this.addEventListener('keydown', this.handleTransitionTabKeydown, { signal });
      this.addEventListener('pointerover', this.handlePointerOver, { signal });
      this.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.addEventListener('transitionend', this.handleAlignedTransitionEnd, { signal });
      this.addEventListener('transitioncancel', this.handleAlignedTransitionEnd, { signal });
      window.addEventListener('dieux:swiper:ready', this.mountActiveCarousel, { signal });
      if (this.dataset.productListSource === 'bundle_components') {
        document.addEventListener('dieux:product-variant-change', this.handleProductVariantChange, {
          signal,
        });
      }

      this.metricsObserver = new ResizeObserver(this.scheduleMetrics);
      this.metricsObserver.observe(this);

      document.fonts?.ready.then(() => {
        if (!signal.aborted && this.isConnected) this.scheduleLayoutMetrics();
      });

      if (this.isDesignMode()) {
        this.editorObserver = new MutationObserver(this.handleEditorMutations);
        this.observeEditorDom();
      }

      this.reinitialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.transitionGeneration = (this.transitionGeneration || 0) + 1;
      this.transitionPromise = null;
      this.setTransitioning(false);
      this.metricsObserver?.disconnect();
      this.editorObserver?.disconnect();
      this.editorObserver = null;
      window.DieuxFrame?.cancelMeasure(this.layoutMeasureKey);
      this.querySelector('[data-product-list-tabs-owner]')?.removeAttribute(
        'data-dieux-tabs-external-ready'
      );
      cancelAnimationFrame(this.metricsFrame || 0);
      this.metricsFrame = 0;
      cancelAnimationFrame(this.reinitializeFrame || 0);
      this.reinitializeFrame = 0;
      this.activeAnimations?.forEach((animation) => animation.cancel());
      this.activeAnimations?.clear();
      this.carouselController?.destroy();
      this.carouselController = null;
    }

    reinitialize() {
      if (!this.isConnected) return;

      this.setupLists();
      this.observeLayoutTargets();
      if (this.reinitializeFrame) return;

      this.reinitializeFrame = requestAnimationFrame(() => {
        this.reinitializeFrame = 0;
        if (!this.isConnected || this.abortController?.signal.aborted) return;

        this.mountActiveCarousel();
      });
    }

    observeLayoutTargets() {
      if (!this.metricsObserver) return;

      this.metricsObserver.disconnect();
      this.metricsObserver.observe(this);
      this.querySelectorAll(
        '[data-product-card-alignment-slot], [data-product-card-aligned-checklist]'
      ).forEach((target) => this.metricsObserver.observe(target));
    }

    get tabs() {
      return [...this.querySelectorAll('[data-product-list-tab]')];
    }

    get panels() {
      return [...this.querySelectorAll('[data-product-list-panel]')];
    }

    get activePanel() {
      return this.panels.find((panel) => !panel.hidden);
    }

    isDesignMode() {
      return document.documentElement.classList.contains('shopify-design-mode');
    }

    observeEditorDom() {
      this.editorObserver?.observe(this, { childList: true, subtree: true });
    }

    handleEditorMutations = (mutations) => {
      const hasElementChange = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some((node) => node.nodeType === 1)
      );
      if (hasElementChange) this.reinitialize();
    };

    reconcileEditorDom() {
      if (!this.isDesignMode() || !this.hasAttribute('data-product-list-editor-reconcile')) return;
      const sectionWrapper = this.closest('[id^="shopify-section-"]') || this;
      const panelsRoot = this.querySelector('[data-product-list-panels]');
      if (!panelsRoot) return;

      this.editorObserver?.disconnect();
      try {
        [...sectionWrapper.querySelectorAll('[data-product-list-panel]')].forEach((panel) => {
          if (!panelsRoot.contains(panel)) panelsRoot.appendChild(panel);
        });
        const panels = this.panels;

        [sectionWrapper, this, panelsRoot].forEach((container) => {
          [...container.childNodes].forEach((node) => {
            if (node.nodeType === Node.COMMENT_NODE) {
              if (node.textContent.includes('product-list-category')) node.remove();
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
              node.remove();
            }
          });
        });

        let tablist = this.querySelector('[data-product-list-tabs]');
        if (!tablist && panels.length > 1) tablist = this.buildEditorTabsShell();
        if (!tablist) {
          [...sectionWrapper.querySelectorAll('[data-product-list-tab]')].forEach((tab) =>
            tab.remove()
          );
          return;
        }

        const strayTabs = [...sectionWrapper.querySelectorAll('[data-product-list-tab]')].filter(
          (tab) => !tablist.contains(tab)
        );
        const previousTabs = [...tablist.children];

        panels.forEach((panel, index) => {
          const panelId = panel.dataset.productListPanel;
          const stray = strayTabs.find((tab) => tab.dataset.productListTab === panelId);
          const existing = [...tablist.children].find(
            (tab) => tab.dataset.productListTab === panelId
          );
          if (stray && existing) existing.remove();
          tablist.appendChild(stray || existing || this.buildEditorTab(panel, index));
          panel.removeAttribute('__product_list_panel_attributes__');
        });

        [...tablist.children].forEach((tab) => {
          const panelExists = panels.some(
            (panel) => panel.dataset.productListPanel === tab.dataset.productListTab
          );
          if (!panelExists) tab.remove();
        });
        strayTabs.forEach((tab) => {
          if (!tablist.contains(tab)) tab.remove();
        });

        const currentTabs = [...tablist.children];
        const tablistChanged =
          currentTabs.length !== previousTabs.length ||
          currentTabs.some((tab, index) => tab !== previousTabs[index]);
        if (tablistChanged) {
          const owner = this.querySelector('[data-product-list-tabs-owner]');
          if (owner) {
            owner.removeAttribute('data-dieux-tabs-external-ready');
            const parent = owner.parentNode;
            const anchor = owner.nextSibling;
            owner.remove();
            parent.insertBefore(owner, anchor);
          }
        }
      } finally {
        this.observeEditorDom();
      }
    }

    buildEditorTabsShell() {
      const owner = document.createElement('dieux-tabs');
      owner.className = 'dieux-tabs product-list-categories__tabs';
      owner.setAttribute('data-dieux-tabs-external', '');
      owner.setAttribute('data-product-list-tabs-owner', '');
      owner.hidden = true;

      const rail = document.createElement('dieux-tab-rail');
      rail.className = 'dieux-tabs__rail';
      rail.setAttribute('data-swiper-state', 'static');
      rail.setAttribute('data-can-scroll-previous', 'false');
      rail.setAttribute('data-can-scroll-next', 'true');

      const viewport = document.createElement('div');
      viewport.className = 'product-list-categories__tabs-viewport dieux-tabs__viewport swiper';

      const tablist = document.createElement('div');
      tablist.className = 'product-list-categories__tablist dieux-tabs__tablist swiper-wrapper';
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', this.dataset.productListLabel || '');
      tablist.setAttribute('data-product-list-tabs', '');
      viewport.append(tablist);
      rail.append(viewport);
      owner.append(rail);

      let headerRow = this.querySelector('.product-list-categories__header-row');
      if (!headerRow) {
        const header = document.createElement('header');
        header.className = 'product-list__header g-section-container';
        headerRow = document.createElement('div');
        headerRow.className = 'product-list__header-row product-list-categories__header-row';
        header.append(headerRow);
        this.prepend(header);
      }
      const links = headerRow.querySelector('[data-product-list-links]');
      headerRow.insertBefore(owner, links);
      return tablist;
    }

    buildEditorTab(panel, index) {
      const blockId = panel.dataset.productListPanel;
      const label =
        panel.dataset.productListTitle?.trim() ||
        `${this.dataset.productListLabel || ''} ${index + 1}`.trim();

      const tab = document.createElement('button');
      tab.className = 'dieux-tabs__tab swiper-slide';
      tab.id = `ProductListTab-${this.dataset.sectionId}-${blockId}`;
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.dataset.productListTab = blockId;
      tab.dataset.tabValue = blockId;
      tab.dataset.clickName = 'Product List - Category Tab';
      tab.dataset.clickLinkName = label;
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', 'false');
      tab.tabIndex = -1;
      tab.disabled = true;

      const stack = document.createElement('span');
      stack.className = 'dieux-tabs__label-stack';
      const measure = document.createElement('span');
      measure.className = 'dieux-tabs__label-measure';
      measure.setAttribute('aria-hidden', 'true');
      measure.textContent = label;
      const text = document.createElement('span');
      text.className = 'dieux-tabs__label';
      text.setAttribute('data-product-list-tab-label', '');
      text.textContent = label;
      stack.append(measure, text);
      tab.append(stack);
      return tab;
    }

    setupLists() {
      this.reconcileEditorDom();
      const panels = this.panels;
      const tabs = this.tabs;
      const tabsOwner = this.querySelector('[data-product-list-tabs-owner]');

      if (!panels.length) {
        if (!document.documentElement.classList.contains('shopify-design-mode')) this.hidden = true;
        this.dataset.productListReady = 'true';
        return;
      }

      const hasCategories = panels.length > 1 && tabs.length === panels.length;
      this.dataset.productListPanelCount = String(panels.length);
      this.toggleAttribute('data-product-list-has-categories', hasCategories);
      if (tabsOwner) tabsOwner.hidden = !hasCategories;
      this.normalizeCategoryLinks(hasCategories, tabsOwner);

      panels.forEach((panel) => {
        panel.querySelector('[data-product-list-viewport]')?.setAttribute('aria-busy', 'false');

        if (hasCategories) {
          const tab = tabs.find((candidate) => candidate.dataset.productListTab === panel.dataset.productListPanel);
          panel.setAttribute('role', 'tabpanel');
          panel.removeAttribute('aria-label');
          if (tab?.id) panel.setAttribute('aria-labelledby', tab.id);
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.removeAttribute('aria-label');
          panel.removeAttribute('tabindex');
        }
      });

      let activePanel = panels.find(
        (panel) => panel.dataset.productListPanel === this.currentPanelId
      );
      if (!activePanel) activePanel = panels.find((panel) => !panel.hidden) || panels[0];
      if (this.isDesignMode()) {
        panels.forEach((panel) => {
          panel.hidden = hasCategories && panel !== activePanel;
        });
      }
      this.currentPanelId = activePanel.dataset.productListPanel;
      this.requestedPanelId = this.currentPanelId;
      this.dataset.productListCurrentCategory = this.currentPanelId;
      this.dataset.productListReady = 'true';
      this.setSelectedTab(this.currentPanelId);
      this.enableSharedTabs();
    }

    normalizeCategoryLinks(hasCategories, tabsOwner) {
      const links = this.querySelector('[data-product-list-links]');
      const tablist = tabsOwner?.querySelector('[data-product-list-tabs]');
      const headerRow = this.querySelector('.product-list-categories__header-row');
      if (!links || !headerRow) return;

      if (hasCategories && tablist) {
        links.classList.remove('swiper-slide');
        links.removeAttribute('role');
        const viewport = tablist.parentElement;
        if (viewport && links.parentElement !== viewport) viewport.append(links);
        return;
      }

      links.classList.remove('swiper-slide');
      links.removeAttribute('role');
      if (links.parentElement !== headerRow) headerRow.append(links);
    }

    enableSharedTabs() {
      const owner = this.querySelector('[data-product-list-tabs-owner]');
      const signal = this.abortController?.signal;
      if (!owner || signal?.aborted) return;
      (window.importShim ?? ((specifier) => import(specifier)))('@theme/dieux-tabs').catch(() => {});

      customElements.whenDefined('dieux-tabs').then(() => {
        if (signal?.aborted || !this.isConnected || !owner.isConnected) return;
        this.selectSharedTab(this.currentPanelId);
        this.tabs.forEach((tab) => {
          tab.disabled = false;
        });
        owner.setAttribute('data-dieux-tabs-external-ready', '');
        this.syncPanelStops();
      });
    }

    syncPanelStops() {
      const signal = this.abortController?.signal;
      const frame = window.DieuxFrame;
      if (!frame || signal?.aborted || !this.isConnected) return;

      this.panelStopsKey ??= Symbol('product-list-panel-stops');
      frame.syncTabStops?.(this.panelStopsKey, {
        targets: () => this.panels.filter((panel) => panel.getAttribute('role') === 'tabpanel'),
        needsStop: (panel) => !frame.hasTabbable(panel, { visible: true }),
        isCurrent: () => !signal?.aborted && this.isConnected,
      });
    }

    handleTabChange = (event) => {
      if (event.target.closest?.('[data-product-card]')) {
        this.scheduleLayoutMetrics();
        return;
      }

      const owner = this.querySelector('[data-product-list-tabs-owner]');
      if (event.target !== owner) return;

      const blockId = event.detail?.value;
      if (!blockId) return;
      this.activate(blockId, { syncTabs: false });
    };

    handlePointerOver = (event) => {
      const tab = event.target.closest('[data-product-list-tab]');
      if (!tab || !this.contains(tab) || tab.contains(event.relatedTarget)) return;
      this.warmPanel(this.panelFor(tab.dataset.productListTab), 'intent');
    };

    handlePointerDown = (event) => {
      const tab = event.target.closest('[data-product-list-tab]');
      if (!tab || !this.contains(tab)) return;
      this.warmPanel(this.panelFor(tab.dataset.productListTab), 'activation');
    };

    handleProductVariantChange = (event) => {
      if (String(event.detail?.productId || '') !== String(this.dataset.productId || '')) return;

      this.selectBundleVariant(event.detail?.variant?.id);
    };

    selectBundleVariant(variantId) {
      const key = String(variantId || '');
      let revealedPanel = null;

      this.carouselController?.destroy();
      this.carouselController = null;
      this.panels.forEach((panel) => {
        const isMatch = panel.dataset.productListBundleVariant === key;
        panel.toggleAttribute('hidden', !isMatch);
        panel.toggleAttribute('inert', !isMatch);
        if (isMatch) revealedPanel = panel;
      });

      this.toggleAttribute('hidden', !revealedPanel);
      if (!revealedPanel) return;

      this.currentPanelId = revealedPanel.dataset.productListPanel;
      this.requestedPanelId = this.currentPanelId;
      this.dataset.productListCurrentCategory = this.currentPanelId;
      requestAnimationFrame(() => {
        if (this.isConnected && !revealedPanel.hidden) this.mountActiveCarousel();
      });
    }

    handleTransitionTabKeydown = (event) => {
      const tab = event.target.closest('[data-product-list-tab]');
      if (!tab || !this.contains(tab)) return;

      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        tab.hasAttribute('data-dieux-tabs-requested') &&
        tab.dataset.productListTab !== this.currentPanelId
      ) {
        event.preventDefault();
        Promise.resolve(this.transitionPromise).then(() => {
          if (!this.isConnected) return;
          this.activePanelFocusTarget()?.focus({ preventScroll: true });
        });
      }
    };

    panelFor(blockId) {
      return this.panels.find((panel) => panel.dataset.productListPanel === blockId);
    }

    selectSharedTab(blockId, { focus = false } = {}) {
      const selectedIndex = this.tabs.findIndex((tab) => tab.dataset.productListTab === blockId);
      if (selectedIndex < 0) return;

      const owner = this.querySelector('[data-product-list-tabs-owner]');
      if (typeof owner?.selectTab === 'function' && owner.tabs?.length) {
        owner.selectTab(selectedIndex, { emit: false, focus });
        return;
      }

      this.tabs.forEach((tab) => {
        const selected = tab.dataset.productListTab === blockId;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
    }

    requestSharedTab(blockId, { focus = false } = {}) {
      const requestedIndex = this.tabs.findIndex((tab) => tab.dataset.productListTab === blockId);
      if (requestedIndex < 0) return;

      const owner = this.querySelector('[data-product-list-tabs-owner]');
      if (typeof owner?.setExternalIntent === 'function' && owner.tabs?.length) {
        owner.setExternalIntent(requestedIndex, { emit: false });
        if (focus) this.tabs[requestedIndex].focus();
        return;
      }

      this.tabs.forEach((tab, index) => {
        tab.tabIndex = index === requestedIndex ? 0 : -1;
      });
      if (focus) this.tabs[requestedIndex].focus();
    }

    setRequestedTab(blockId, { focus = false, syncTabs = true } = {}) {
      this.tabs.forEach((tab) => {
        tab.toggleAttribute('data-dieux-tabs-requested', tab.dataset.productListTab === blockId);
      });
      if (syncTabs) this.requestSharedTab(blockId, { focus });
    }

    setSelectedTab(blockId) {
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.productListTab === blockId;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        tab.removeAttribute('data-dieux-tabs-requested');
      });
    }

    activate(blockId, { focus = false, syncTabs = true } = {}) {
      const nextPanel = this.panelFor(blockId);
      if (!nextPanel) return;

      this.requestedPanelId = blockId;
      this.setRequestedTab(blockId, { focus, syncTabs });
      this.warmPanel(nextPanel, 'activation');

      if (this.currentPanelId === blockId && !this.transitionPromise) {
        this.setSelectedTab(blockId);
        return Promise.resolve();
      }
      if (!this.transitionPromise) {
        const transition = this.runTransitions(this.transitionGeneration);
        this.transitionPromise = transition;
        const clearTransition = () => {
          if (this.transitionPromise === transition) this.transitionPromise = null;
        };
        transition.then(clearTransition, clearTransition);
      }
      return this.transitionPromise;
    }

    setTransitioning(isBusy) {
      this.toggleAttribute('data-category-transitioning', isBusy);
      this.querySelector('[data-product-list-tabs-owner]')?.setIndicatorTransitioning?.(isBusy);
      this.panels.forEach((panel) => {
        panel.querySelector('[data-product-list-viewport]')?.setAttribute('aria-busy', String(isBusy && !panel.hidden));
      });
    }

    activePanelFocusTarget(panel = this.activePanel) {
      const ctaLink = this.querySelector('[data-product-list-links] a');
      if (ctaLink?.getClientRects().length) return ctaLink;
      const frame = window.DieuxFrame;
      if (!panel || !frame || panel.getAttribute('tabindex') === '0') return panel;
      const firstContent = [...panel.querySelectorAll(frame.tabbableSelector)].find(
        (element) => !element.closest('[aria-hidden="true"]') && frame.isTabbable(element, { visible: true })
      );
      return firstContent || panel;
    }

    animateSlides(slides, direction) {
      if (this.reducedMotion.matches || typeof slides[0]?.animate !== 'function') {
        return Promise.resolve([]);
      }

      const entering = direction === 'in';
      const keyframes = entering
        ? [
            { opacity: 0, transform: 'translateY(8px) scale(0.994)' },
            { opacity: 1, transform: 'translateY(0) scale(1)' },
          ]
        : [
            { opacity: 1, transform: 'translateY(0) scale(1)' },
            { opacity: 0, transform: 'translateY(-6px) scale(0.996)' },
          ];
      const duration = entering ? 220 : 110;
      const stagger = entering ? 30 : 18;
      const easing = entering ? 'cubic-bezier(.32, .06, .22, 1)' : 'cubic-bezier(.4, 0, 1, 1)';
      const animations = slides.map((slide, index) => {
        const animation = slide.animate(keyframes, {
          duration,
          delay: index * stagger,
          easing,
          fill: 'both',
        });
        this.activeAnimations.add(animation);
        return animation;
      });

      if (entering) deferAnimationsToFirstPaint(animations);

      return Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(
        () => animations
      );
    }

    clearAnimations(animations) {
      animations.forEach((animation) => {
        this.activeAnimations.delete(animation);
        animation.cancel();
      });
    }

    async runTransitions(generation) {
      this.setTransitioning(true);

      try {
        while (
          this.currentPanelId !== this.requestedPanelId
          && this.isConnected
          && generation === this.transitionGeneration
        ) {
          const outgoingPanel = this.activePanel;
          const requestedPanel = this.panelFor(this.requestedPanelId);
          if (!outgoingPanel || !requestedPanel) break;

          const outgoingSlides = [...outgoingPanel.querySelectorAll('.product-list__slide')];
          const [outgoingAnimations] = await Promise.all([
            this.animateSlides(outgoingSlides, 'out'),
            this.warmPanel(requestedPanel, 'activation'),
          ]);
          if (!this.isConnected || generation !== this.transitionGeneration) {
            this.clearAnimations(outgoingAnimations);
            break;
          }

          if (this.currentPanelId === this.requestedPanelId) {
            this.clearAnimations(outgoingAnimations);
            this.clearAnimations(await this.animateSlides(outgoingSlides, 'in'));
            this.setSelectedTab(this.currentPanelId);
            continue;
          }

          const nextPanel = this.panelFor(this.requestedPanelId);
          if (!nextPanel) {
            this.clearAnimations(outgoingAnimations);
            break;
          }

          const activeElement = document.activeElement;
          const links = this.querySelector('[data-product-list-links]');
          const shouldMoveFocus =
            outgoingPanel.contains(activeElement) || links?.contains(activeElement);

          outgoingPanel.hidden = true;
          nextPanel.hidden = false;
          this.currentPanelId = this.requestedPanelId;
          this.dataset.productListCurrentCategory = this.currentPanelId;
          this.setSelectedTab(this.currentPanelId);
          const nextViewport = nextPanel.querySelector('[data-product-list-viewport]');
          if (nextViewport) nextViewport.scrollLeft = 0;
          this.setTransitioning(true);
          if (shouldMoveFocus) {
            this.activePanelFocusTarget(nextPanel)?.focus({ preventScroll: true });
          }
          this.mountActiveCarousel();
          this.clearAnimations(outgoingAnimations);

          if (!this.reducedMotion.matches) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
          }
          if (!this.isConnected || generation !== this.transitionGeneration) break;
          this.clearAnimations(
            await this.animateSlides([...nextPanel.querySelectorAll('.product-list__slide')], 'in')
          );
        }

        if (generation !== this.transitionGeneration) return;
        const selectedLabel = this.tabs
          .find((tab) => tab.dataset.productListTab === this.currentPanelId)
          ?.querySelector('[data-product-list-tab-label]')
          ?.textContent.trim();
        const status = this.querySelector('[data-product-list-status]');
        if (status && selectedLabel) {
          const messageTemplate = this.dataset.productListStatusTemplate;
          status.textContent = messageTemplate
            ? messageTemplate.replace('__CATEGORY__', () => selectedLabel)
            : selectedLabel;
        }
      } finally {
        if (generation === this.transitionGeneration) {
          this.activeAnimations.forEach((animation) => animation.cancel());
          this.activeAnimations.clear();
          this.setTransitioning(false);
        }
      }
    }

    warmPanel(panel, mode = 'intent') {
      if (!panel) return Promise.resolve();
      const images = [...panel.querySelectorAll('.product-card-media__image--primary')].slice(0, 2);
      if (mode !== 'activation') {
        prefetchSwapImages(images, { intent: true });
        return Promise.resolve();
      }
      images.forEach((image) => {
        image.fetchPriority = 'high';
        image.loading = 'eager';
      });

      const firstImage = images[0];
      if (!firstImage?.decode) return Promise.resolve();
      return Promise.race([
        firstImage.decode().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 180)),
      ]);
    }

    scheduleMetrics = () => {
      this.scheduleLayoutMetrics();
      if (this.metricsFrame) return;
      this.metricsFrame = requestAnimationFrame(() => {
        this.metricsFrame = 0;
        this.syncCarouselMetrics();
      });
    };

    scheduleLayoutMetrics = () => {
      const frame = window.DieuxFrame;
      if (!frame) return;

      this.layoutMeasureKey ??= Symbol('product-list-layout-metrics');
      frame.measure(this.layoutMeasureKey, this.measureLayoutMetrics);
    };

    measureLayoutMetrics = () => {
      const frame = window.DieuxFrame;
      if (!frame || !this.isConnected) return;

      const alignedBlockUpdates = new Map();
      const alignedQualityUpdates = new Map();
      const alignedChecklistUpdates = new Map();
      const allAlignedBlocks = [...this.querySelectorAll('[data-product-card-alignment-slot]')];
      const allAlignedQualityRows = [
        ...this.querySelectorAll('.product-card-qualities__item'),
      ];
      const allAlignedChecklistRows = [
        ...this.querySelectorAll('[data-product-card-aligned-checklist] li'),
      ];
      const leavingChecklistRows = new Set(
        allAlignedChecklistRows.filter((item) => {
          const tabPanel = item.closest('.dieux-tabs__panel');
          return tabPanel && !tabPanel.hidden && tabPanel.dataset.current !== 'true';
        })
      );
      const currentChecklists = new Set();
      this.panels
        .filter((panel) => !panel.hidden && panel.getClientRects().length)
        .forEach((panel) => {
          const cardRows = [];
          panel
            .querySelectorAll(
              '[data-product-card][data-product-card-context~="aligned-blocks"]'
            )
            .forEach((card) => {
              if (!card.getClientRects().length) return;

              const blockStart = card.getBoundingClientRect().top;
              let cardRow = cardRows.find((row) => Math.abs(row.blockStart - blockStart) <= 1);
              if (!cardRow) {
                cardRow = { blockStart, cards: [] };
                cardRows.push(cardRow);
              }
              cardRow.cards.push(card);
            });

          cardRows.forEach(({ cards }) => {
            const blockGroups = new Map();
            const qualityRows = [];
            const checklistRows = [];

            cards.forEach((card) => {
              const blocks = [
                ...card.querySelectorAll(':scope > [data-product-card-alignment-slot]'),
              ];
              const numericSuffixes = blocks.map((block) =>
                block.dataset.productCardAlignmentSlot?.match(/-\d+$/)?.[0] || ''
              );
              const repeatedSuffix =
                numericSuffixes[0]
                && numericSuffixes.every((suffix) => suffix === numericSuffixes[0])
                  ? numericSuffixes[0]
                  : '';

              blocks.forEach((block) => {
                if (!block.getClientRects().length) return;

                const rawSlot = block.dataset.productCardAlignmentSlot || '';
                const slot = repeatedSuffix
                  ? rawSlot.slice(0, -repeatedSuffix.length)
                  : rawSlot;
                if (!slot) return;
                if (!blockGroups.has(slot)) blockGroups.set(slot, []);
                blockGroups.get(slot).push(this.measureAlignedBlock(block));
              });

              card.querySelectorAll('.product-card-qualities').forEach((list) => {
                if (!list.getClientRects().length) return;
                [...list.querySelectorAll('.product-card-qualities__item')].forEach(
                  (item, index) => {
                    qualityRows[index] ??= [];
                    qualityRows[index].push(this.measureAlignedRow(item));
                  }
                );
              });

              card
                .querySelectorAll(
                  '.dieux-tabs__panel[data-current="true"] [data-product-card-aligned-checklist]'
                )
                .forEach((list) => {
                  if (!list.getClientRects().length) return;
                  currentChecklists.add(list);
                  [...list.querySelectorAll('li')].forEach((item, index) => {
                    checklistRows[index] ??= [];
                    checklistRows[index].push(this.measureAlignedRow(item, '::before'));
                  });
                });
            });

            blockGroups.forEach((group) => {
              this.collectAlignedUpdates(group, alignedBlockUpdates);
            });
            qualityRows.forEach((row) => {
              this.collectAlignedUpdates(row, alignedQualityUpdates);
            });
            checklistRows.forEach((row) => {
              this.collectAlignedUpdates(row, alignedChecklistUpdates, { alignContent: true });
            });
          });
        });

      const previousChecklists = this.alignedChecklists;
      this.alignedChecklists = currentChecklists;
      const followSettledChecklists =
        Boolean(previousChecklists)
        && [...currentChecklists].some((list) => !previousChecklists.has(list));

      frame.write(() => {
        allAlignedBlocks.forEach((block) => {
          if (!block.isConnected) return;
          this.applyAlignedSize(
            block,
            '--product-card-aligned-block-size',
            alignedBlockUpdates.get(block)
          );
        });
        const aligningCards = new Set();
        allAlignedChecklistRows.forEach((item) => {
          if (!item.isConnected || leavingChecklistRows.has(item)) return;
          const property = '--product-card-featured-tabs-aligned-row-size';
          const size = alignedChecklistUpdates.get(item);
          const list = item.closest('[data-product-card-aligned-checklist]');
          if (
            followSettledChecklists
            && previousChecklists.has(list)
            && item.style.getPropertyValue(property) !== (size || '')
          ) {
            const card = item.closest('[data-product-card]');
            if (card) aligningCards.add(card);
          }
          this.applyAlignedSize(item, property, size);
        });
        aligningCards.forEach((card) => {
          card.setAttribute('data-product-card-aligning', '');
          frame.afterPaint(() => this.releaseAligningCard(card));
        });
        allAlignedQualityRows.forEach((item) => {
          if (!item.isConnected) return;
          this.applyAlignedSize(
            item,
            '--product-card-qualities-aligned-row-size',
            alignedQualityUpdates.get(item)
          );
        });
      });
    };

    measureAlignedBlock(block) {
      const blockRect = block.getBoundingClientRect();
      const styles = getComputedStyle(block);
      const inset = this.blockInsets(styles);
      const contentEnd = [...block.children]
        .filter((child) => child.getClientRects().length && child.tagName !== 'TEMPLATE')
        .reduce((maximum, child) => {
          const childRect = child.getBoundingClientRect();
          return Math.max(maximum, childRect.bottom - blockRect.top);
        }, inset.blockStart);

      return {
        item: block,
        inset: inset.total,
        outerSize: Math.max(inset.total, contentEnd + inset.blockEnd),
        borderBox: styles.boxSizing === 'border-box',
      };
    }

    measureAlignedRow(item, pseudoElement) {
      const styles = getComputedStyle(item);
      const inset = this.blockInsets(styles);
      const pseudoSize = pseudoElement
        ? Number.parseFloat(getComputedStyle(item, pseudoElement).blockSize) || 0
        : 0;
      const range = document.createRange();
      range.selectNodeContents(item);
      const contentRects = [...range.getClientRects()];
      const lineSize = Number.parseFloat(styles.lineHeight) || 0;
      const contentSize = contentRects.length
        ? Math.max(...contentRects.map((rect) => rect.top))
          - Math.min(...contentRects.map((rect) => rect.top))
          + lineSize
        : 0;
      range.detach();

      return {
        item,
        inset: inset.total,
        outerSize: Math.max(contentSize, pseudoSize) + inset.total,
        borderBox: styles.boxSizing === 'border-box',
      };
    }

    blockInsets(styles) {
      const blockStart =
        (Number.parseFloat(styles.paddingBlockStart) || 0)
        + (Number.parseFloat(styles.borderBlockStartWidth) || 0);
      const blockEnd =
        (Number.parseFloat(styles.paddingBlockEnd) || 0)
        + (Number.parseFloat(styles.borderBlockEndWidth) || 0);
      return { blockStart, blockEnd, total: blockStart + blockEnd };
    }

    collectAlignedUpdates(group, updates, { alignContent = false } = {}) {
      if (group.length < 2) return;

      const outerMaximum = Math.max(...group.map(({ outerSize }) => outerSize));
      const contentMaximum = Math.max(...group.map(({ outerSize, inset }) => outerSize - inset));
      group.forEach(({ item, inset, borderBox }) => {
        const contentSize = alignContent ? contentMaximum : Math.max(0, outerMaximum - inset);
        const size = borderBox ? contentSize + inset : contentSize;
        const rounded = `${Math.ceil(size * 100) / 100}px`;
        updates.set(item, rounded);
      });
    }

    handleAlignedTransitionEnd = (event) => {
      if (!event.target.matches?.('[data-product-card-aligned-checklist] li')) return;
      const card = event.target.closest('[data-product-card-aligning]');
      if (card && this.contains(card)) this.releaseAligningCard(card);
    };

    releaseAligningCard(card) {
      const frame = window.DieuxFrame;
      if (!frame) return;
      frame.read(() => {
        if (!card.isConnected || !card.hasAttribute('data-product-card-aligning')) return;
        const running = card
          .getAnimations({ subtree: true })
          .some(
            (animation) =>
              animation instanceof CSSTransition
              && animation.effect?.target?.matches('[data-product-card-aligned-checklist] li')
          );
        if (running) return;
        frame.write(() => card.removeAttribute('data-product-card-aligning'));
      });
    }

    applyAlignedSize(item, property, size) {
      if (!size) {
        item.style.removeProperty(property);
        return;
      }
      if (item.style.getPropertyValue(property) === size) return;
      item.style.setProperty(property, size);
    }

    syncCarouselMetrics() {
      const responsiveMode = this.getResponsiveMode();
      if (responsiveMode !== this.responsiveMode) {
        this.mountActiveCarousel();
        return;
      }

      this.carouselController?.refresh(false);
    }

    getResponsiveMode() {
      const frame =
        this.activePanel?.querySelector('[data-product-list-carousel]')
        || this.querySelector('[data-product-list-carousel]');
      return (
        getComputedStyle(frame || this).getPropertyValue('--product-list-responsive-mode').trim()
        || 'rail'
      );
    }

    isDesktopGrid() {
      return this.getResponsiveMode() === 'grid';
    }

    mountActiveCarousel = () => {
      this.scheduleLayoutMetrics();
      this.syncPanelStops();
      this.responsiveMode = this.getResponsiveMode();
      this.carouselController?.destroy();
      this.carouselController = null;

      if (this.isDesktopGrid()) {
        this.dataset.swiperState = 'static';
        requestAnimationFrame(() => {
          if (!this.isConnected || !this.isDesktopGrid()) return;
          this.querySelectorAll('[data-product-list-viewport]').forEach((viewport) => {
            viewport.scrollLeft = 0;
          });
        });
        return;
      }

      const panel = this.activePanel;
      const carousel = panel?.querySelector('[data-product-list-carousel]');
      const viewport = carousel?.querySelector(':scope > [data-product-list-viewport]');
      const track = viewport?.querySelector(':scope > [data-product-list-track]');
      if (!panel || !carousel || !viewport || !track || !window.DieuxSwiper) return;

      this.carouselController = window.DieuxSwiper.mount({
        owner: this,
        viewport,
        track,
        stateTarget: this,
        previousButton: carousel.querySelector(':scope > [data-swiper-button="previous"]'),
        nextButton: carousel.querySelector(':scope > [data-swiper-button="next"]'),
        mousewheel: true,
        gapProperty: '--product-list-gap-current',
        gapSource: this,
        options: {
          slidesPerView: 'auto',
          a11y: {
            enabled: false,
          },
        },
        onStateChange: ({ state, controller }) => {
          if (state === 'enhanced') requestAnimationFrame(() => controller.refresh());
        },
      });
    };

    editorBlockElement(panel, blockId, sourceElement) {
      const sourceBlock =
        sourceElement instanceof Element
          ? sourceElement.closest('[data-shopify-editor-block]')
          : null;
      if (sourceBlock && panel.contains(sourceBlock)) return sourceBlock;

      return [...panel.querySelectorAll('[data-shopify-editor-block]')].find((element) => {
        try {
          return JSON.parse(element.dataset.shopifyEditorBlock).id === blockId;
        } catch (_error) {
          return false;
        }
      });
    }

    alignEditorBlock(panel, blockId, sourceElement) {
      if (this.isDesktopGrid()) return;

      const selectedBlock = this.editorBlockElement(panel, blockId, sourceElement);
      const slide = selectedBlock?.closest('.product-list__slide');
      const viewport = panel.querySelector('[data-product-list-viewport]');
      if (!(slide instanceof HTMLElement) || !(viewport instanceof HTMLElement)) return;

      const align = () => {
        if (!this.isConnected || panel.hidden) return;

        const slides = [...viewport.querySelectorAll('.product-list__slide')];
        const slideIndex = slides.indexOf(slide);
        if (slideIndex < 0) return;

        const instance = this.carouselController?.instance;
        if (instance && !instance.destroyed) {
          viewport.scrollLeft = 0;
          instance.slideTo(slideIndex, 0);
        } else {
          viewport.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
        }
      };

      Promise.resolve(this.carouselController?.promote()).then(() => {
        requestAnimationFrame(align);
      });
    }

    revealBlock(blockId, sourceElement) {
      if (!blockId) return;

      let directPanel =
        sourceElement instanceof Element
          ? sourceElement.closest('[data-product-list-panel]')
          : null;
      if (directPanel && !this.contains(directPanel)) directPanel = null;
      if (!directPanel) {
        directPanel = this.panels.find((panel) => panel.dataset.productListPanel === blockId);
      }
      if (!directPanel) {
        directPanel = this.panels.find((panel) =>
          [...panel.querySelectorAll('[data-shopify-editor-block]')].some((element) => {
            try {
              return JSON.parse(element.dataset.shopifyEditorBlock).id === blockId;
            } catch (_error) {
              return false;
            }
          })
        );
      }
      if (directPanel) {
        Promise.resolve(this.activate(directPanel.dataset.productListPanel)).then(() => {
          this.alignEditorBlock(directPanel, blockId, sourceElement);
        });
      } else {
        this.carouselController?.promote();
      }
    }
  }

  customElements.define('product-list', ProductList);
})();
